import { useState, useCallback, useMemo } from 'react';
import { NumberKeyState, MButtonState, ABPoints } from '../types';
import { useNumberKeys } from './useNumberKeys';

// MainPage 特定的配置
interface MainPageNumberKeyConfig {
  maxNumberKeys: number;
  keySetIndex: number;
  ppPlayDuration: number;
  playbackRate: number;
  showTimecode: boolean;
  isUserLoggedIn: boolean;
  currentTab: number;
  tabNumberKeys: Record<number, Record<string, NumberKeyState>>;
  // 新增：MainPage 特定的狀態
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  videoUrl: string;
  user: any;
}

// MainPage 特定的事件處理器
interface MainPageNumberKeyHandlers {
  onNumberKeyClick: (keyNo: number) => Promise<void>;
  onDeleteModeToggle: () => void;
  onSettings: () => void;
  onMButtonClick: () => void;
  onModeChange: (newMode: MButtonState) => void;
  onNoteToggle: () => void;
  onCheckClick: () => void;
  onDeleteButtonClick: () => void;
  onDeleteButtonPress: (e: React.MouseEvent) => void;
  onDeleteButtonRelease: (e: React.MouseEvent) => void;
  onBookmarkClick: () => void;
  onSwitchClick: () => void;
  onBButtonClick: () => void;
  onSpeedChange: (speed: number) => void;
  onThemeDialogOpen?: (key: number) => void;
  onOpenNoteRecords?: () => void;
  onRequestLogin?: () => void;
}

/**
 * MainPage 專用的數字鍵管理 Hook
 * 整合了 MainPage 特定的邏輯和狀態管理
 */
export const useMainPageNumberKeys = (
  config: MainPageNumberKeyConfig,
  handlers: MainPageNumberKeyHandlers
) => {
  // 使用基礎的 useNumberKeys Hook
  const {
    numberKeys,
    isDeleteMode,
    mButtonState,
    vbids,
    setNumberKeys,
    setIsDeleteMode,
    setMButtonState,
    setVbids,
    getSButtonStyle
  } = useNumberKeys(
    {
      maxNumberKeys: config.maxNumberKeys,
      keySetIndex: config.keySetIndex,
      ppPlayDuration: config.ppPlayDuration,
      playbackRate: config.playbackRate,
      showTimecode: config.showTimecode,
      isUserLoggedIn: config.isUserLoggedIn
    },
    handlers
  );

  // 根據當前 Tab 獲取對應的數字鍵
  const currentTabNumberKeys = useMemo(() => {
    return config.tabNumberKeys[config.currentTab] || {};
  }, [config.tabNumberKeys, config.currentTab]);

  // 計算實際最大鍵數
  const actualMaxKeys = useMemo(() => {
    const keys = Object.keys(currentTabNumberKeys);
    if (keys.length === 0) return config.maxNumberKeys;
    
    const maxKey = Math.max(...keys.map(key => parseInt(key)));
    return Math.max(maxKey, config.maxNumberKeys);
  }, [currentTabNumberKeys, config.maxNumberKeys]);

  // 重置數字鍵狀態
  const resetNumberKeys = useCallback(() => {
    setNumberKeys({});
    setVbids([]);
    setIsDeleteMode(false);
  }, [setNumberKeys, setVbids, setIsDeleteMode]);

  // 更新特定 Tab 的數字鍵
  const updateTabNumberKeys = useCallback((tabIndex: number, keys: Record<string, NumberKeyState>) => {
    // 這個函數需要由父組件提供，因為它需要更新 tabNumberKeys 狀態
    console.log('🔄 更新 Tab 數字鍵:', tabIndex, keys);
  }, []);

  // 獲取數字鍵統計信息
  const getNumberKeyStats = useCallback(() => {
    const totalKeys = Object.keys(currentTabNumberKeys).length;
    const activeKeys = Object.values(currentTabNumberKeys).filter(key => key.isActive).length;
    const checkedKeys = Object.values(currentTabNumberKeys).filter(key => key.isChecked).length;
    
    return {
      total: totalKeys,
      active: activeKeys,
      checked: checkedKeys,
      maxKey: actualMaxKeys
    };
  }, [currentTabNumberKeys, actualMaxKeys]);

  return {
    // 基礎狀態
    numberKeys: currentTabNumberKeys,
    isDeleteMode,
    mButtonState,
    vbids,
    
    // 配置
    config: {
      ...config,
      actualMaxKeys
    },
    
    // 事件處理器
    handlers,
    
    // 狀態更新函數
    setNumberKeys,
    setIsDeleteMode,
    setMButtonState,
    setVbids,
    
    // 工具函數
    getSButtonStyle,
    resetNumberKeys,
    updateTabNumberKeys,
    getNumberKeyStats
  };
};

export default useMainPageNumberKeys;
